import React, { useState, useContext, useEffect, useRef } from 'react';
import { store } from 'components/Store';
import { useLocation, Link, NavLink, useParams } from 'react-router-dom';
import LoginButtons from './LoginButtons';
import { useAuth0 } from '@auth0/auth0-react';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import CreateKeywordListButton from './CreateKeywordListButton';
import TrialBanner from './TrialBanner';
import ProfileButton from './ProfileButton';
import NavPages from './NavPages';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import SavedItemsIcon, { useCloseButtonFunctionality } from 'components/SavedItems/ui/SavedItemsIcon';
import { SentimentButtons } from 'components/Dashboard/Sentiment/SentimentGraphPage';
import ProspectiveButton from 'components/Reports/ProspectiveButton';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import ClientNavigation, { useResetClientNavigation } from './ClientNavigation';
import { LoggedOutTopHeader } from './NavPagesLoggedOut';
import NavigationSidebar from './NavigationSideBar';
import dayjs from 'dayjs';
import { regexSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import TopNavSearch from 'components/Dashboard/Navigation/TopNavSearch';
import useDashboardUrlCondition from 'components/Dashboard/Navigation/useDashboardUrlCondition';
import getAppSite from 'utils/getAppSite';
import SnowFall from 'components/Dashboard/Navigation/SnowFall';
import useDetectRateMode from 'components/Dashboard/ListControl/useDetectRateMode';

const christmasCondition = () => {
  const today = dayjs();
  const currentYear = today.year();
  const inJanuary = today.month() === 0;

  const initialDate = dayjs(`December 16 ${inJanuary ? currentYear - 1 : currentYear}`);
  const end = dayjs(`January 2 ${inJanuary ? currentYear : currentYear + 1}`).endOf('day');

  return today.isBefore(end) && today.isAfter(initialDate);
};

const Navigation = () => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { readyKeywordLists, activePlan, isHiddenMenu } = state;
  const { enableReports } = activePlan;

  const location = useLocation();
  const isMobile = useIsMobile();
  const params = useParams();
  const { inboxSection, searchResultsSection } = useMainSectionParameter(params);

  const { isAuthenticated } = useAuth0();
  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();
  const { closeButtonFunctionality } = useCloseButtonFunctionality();
  const { resetClientNavigation } = useResetClientNavigation();
  const { defaultUrl } = useDashboardUrlCondition();
  const homePage = location?.pathname === '/';

  //CREATE FIXED POSITIONS
  useEffect(() => {
    const createFixed = () => {
      const html = document.getElementsByTagName('html')[0];
      let position;
      let classHeader;
      let height;
      let classFavicon;
      let top;
      if (isFixedPathName()) {
        height = `${headerContainer.current?.getBoundingClientRect()?.height}px`;
        if (html.scrollTop > 50) {
          position = 'fixed';
          classHeader = 'header-scrolled';
          top = 0;
        } else {
          position = 'relative';
          top = null;
          classHeader = '';
        }
        if (html.scrollTop > 75) {
          classFavicon = 'logo-header-favicon resized';
          classHeader = 'header-scrolled header-favicon';
        } else {
          classFavicon = '';
        }
      } else {
        position = 'relative';
        top = null;
        height = null;
      }

      setPositonNav({
        position,
        classHeader,
        height,
        top,
        classFavicon,
      });
    };
    document.addEventListener('scroll', createFixed);
    // AE: WE CAN KEEP THIS HERE BECAUSE IS A COMMON COMPONENT OF THE DASHBOARD, TO DO A BETTER LOGIC IT SHOULD BE MOVED TO A BETTER PLACE
    return () => {
      document.removeEventListener('scroll', createFixed);
    };
  }, []);

  const fixedPathNames = ['/', '/pricing', '/for-parliamentarians', '/data-hub', '/live-from-parliament'];
  const [positionNav, setPositonNav] = useState({ position: 'relative', classHeader: '', classFavicon: '' });
  const isFixedPathName = () => {
    let isFixed = false;
    fixedPathNames.forEach((item) => {
      if (item === location.pathname) {
        isFixed = true;
      }
    });
    return isFixed;
  };

  const headerContainer = useRef(null);

  const sentimentPage = location.pathname.toLowerCase() === '/sentiment';
  const slackIntegrationPage = location.pathname.toLowerCase() === '/integration-with-slack';
  const noSpecificPages = !sentimentPage && !slackIntegrationPage;

  const logoUrl = `${
    slackIntegrationPage
      ? '/'
      : isAuthenticated
        ? `/${defaultUrl}${inboxSection ? location.search : ''}${location.hash}`
        : '/'
  }`;

  const conditionTopNav = isAuthenticated || searchResultsSection || regexSectionName(location.pathname) !== null;
  const hamburguerMenu = useRef(null);
  const toggleMenu = () => {
    dispatch({ type: 'SHOW_HIDE_MENU', value: !isHiddenMenu });
  };

  const calculateTransform = () => {
    let topReference = !!headerContainer.current ? headerContainer.current.getBoundingClientRect().top : 0;
    return `-${topReference}px`;
  };

  const headerScrolled = positionNav.classHeader?.includes('header-scrolled');
  const { rateUpdatesMode } = useDetectRateMode();
  const closeRateUpdatesLink = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('mode');
    const querySearch = searchParams.toString();
    return `${location.pathname}${querySearch ? `?${querySearch}` : ''}${location.hash}`;
  };

  return (
    <>
      {!isAuthenticated && getAppSite() !== 'usa' && <LoggedOutTopHeader />}
      <TrialBanner />
      {!isAuthenticated && homePage && <SnowFallContainer isPlaying={true} />}
      <header
        className={`main-header ${!isMobile ? positionNav.classHeader : ''} ${
          isAuthenticated ? 'header-user-logged' : 'header-user-logout'
        }`}
        style={{ position: positionNav.position, top: positionNav.top }}
        ref={headerContainer}
      >
        {(isAuthenticated || (!isAuthenticated && !homePage)) && <SnowFallContainer isPlaying={true} />}
        <>
          {/*LOGO ON MOBILE */}
          <div
            className={`hide-lg px-3 pb-lg-3 header-mobile row mx-0 align-items-center ${
              isAuthenticated || sentimentPage || slackIntegrationPage ? 'justify-content-between ' : ''
            } mobile-loggedout-header`}
          >
            <div style={{ width: '26px' }} />
            <div className={`position-relative ${noSpecificPages ? 'flex-grow-1 text-center' : ''}`}>
              <Link to={logoUrl} className={noSpecificPages ? '' : 'd-lg-inline-block'}>
                <img
                  src={`${import.meta.env.VITE_APP_CDNURL}/images/${
                    christmasCondition() ? 'logo_white-christmas3.png' : 'logo_white.svg'
                  }`}
                  alt='sidebar-icon'
                  className={`${
                    noSpecificPages ? 'logo' : `policy-logo ${christmasCondition() ? 'christmas-logo' : ''}`
                  }`}
                />
              </Link>
            </div>

            {isMobile && (
              <>
                {sentimentPage || slackIntegrationPage ? (
                  <SentimentButtons slackIntegrationPage={slackIntegrationPage} />
                ) : isAuthenticated ? (
                  <ProfileButton />
                ) : (
                  ''
                )}
              </>
            )}
          </div>

          {/*NAV PAGES */}
          <div
            className={`mx-auto main-content-wrapper row mx-auto align-items-center justify-content-between nav-wrapper-container resize-elements-container ${
              conditionTopNav ? '' : 'px-md-5 px-3'
            }`}
          >
            <div
              className={`position-relative show-lg ${
                conditionTopNav
                  ? `side-left-element ${newAgencyPlan ? 'side-left-element-new-agency' : ''} pl-3 pl-lg-5`
                  : ''
              }`}
            >
              {headerScrolled ? (
                <div className='logo-header-scrolled-container'>
                  <Link
                    to={logoUrl}
                    className={`d-lg-inline-block logo-header-scrolled ${positionNav.classFavicon} ${
                      christmasCondition() ? 'logo-part-christmas' : ''
                    }`}
                  >
                    <img
                      className='logo-part logo-part-1'
                      src={`${import.meta.env.VITE_APP_CDNURL}/images/logo-part-1.svg`}
                      alt='logo part 1'
                    />
                    <img
                      className='logo-part logo-part-2'
                      src={`${import.meta.env.VITE_APP_CDNURL}/images/logo-part-2.svg`}
                      alt='logo part 2'
                    />
                    <img
                      className={`logo-part logo-part-3 `}
                      src={`${import.meta.env.VITE_APP_CDNURL}/images/${
                        christmasCondition() ? 'logo-part-3-christmas1.png' : 'logo-part-3.svg'
                      }`}
                      alt='logo part 3'
                    />
                    <img
                      className='logo-part logo-part-4'
                      src={`${import.meta.env.VITE_APP_CDNURL}/images/logo-part-4.svg`}
                      alt='logo part 4'
                    />
                  </Link>
                </div>
              ) : (
                <Link
                  onClick={() => {
                    resetClientNavigation();
                  }}
                  to={logoUrl}
                  className='d-lg-inline-block header-logo-element'
                >
                  <img
                    src={`${import.meta.env.VITE_APP_CDNURL}/images/${
                      christmasCondition() ? 'logo_white-christmas3.png' : 'logo_white.svg'
                    }`}
                    alt='Logo'
                    className={`policy-logo ${christmasCondition() ? 'christmas-logo' : ''}`}
                  />
                </Link>
              )}
            </div>
            {conditionTopNav ? (
              <div className='main-content flex-grow-1 header-container d-flex justify-content-between align-items-center flex-column-reverse flex-lg-row pr-xl-5 px-md-4 px-3'>
                {!isMobile && (
                  <>
                    {noSpecificPages && (
                      <>
                        {newAgencyPlan || rateUpdatesMode ? (
                          <div className='flex-grow-1 flex-centered agency-clients-navigation-container justify-content-between'>
                            {!!newAgencyPlan && <ClientNavigation />}
                            {rateUpdatesMode && (
                              <div
                                className='w-100 flex-centered justify-content-end rounded-pill'
                                style={{ visibility: readyKeywordLists ? 'visible' : 'hidden' }}
                              >
                                <Link
                                  to={closeRateUpdatesLink()}
                                  className='d-inline-block rounded-pill general-button  bg-yellow text-white client-navigation-close-button specific-top-padding-6 reset-link'
                                  onClick={() => {}}
                                >
                                  Close rating mode
                                  <span className='d-inline-block icon-close-bold paragraph-p6' />
                                </Link>
                              </div>
                            )}
                          </div>
                        ) : (
                          <NavPages />
                        )}
                      </>
                    )}
                  </>
                )}

                <div
                  className={`flex-centered justify-content-end main-header-actions-buttons z-1  ${
                    newAgencyPlan || rateUpdatesMode ? 'pl-3 main-header-actions-buttons-agency' : 'flex-grow-1 '
                  }`}
                >
                  {/* SEARCHBAR MODIFIED JUST FOR THE HEADER */}
                  {noSpecificPages && readyKeywordLists && isAuthenticated && (
                    <>
                      <div className={`${newAgencyPlan ? '' : 'pl-lg-1 pl-xl-3 flex-grow-1'}`}>
                        <TopNavSearch />
                      </div>
                      <NavLink
                        to='/calendar'
                        className={({ isActive }) => `calendar-top-nav-icon mr-3 ${isActive ? 'active' : ''}`}
                        data-tooltip-content={location.pathname.startsWith('/calendar') ? '' : 'Calendar'}
                        data-tooltip-id={'general-tooltip'}
                        onClick={() => {
                          closeButtonFunctionality();
                        }}
                      >
                        <span className='icon-calendar' />
                      </NavLink>
                      {!isMobile && (
                        <>
                          <SavedItemsIcon />
                          {agencyUser && enableReports && <ProspectiveButton />}
                        </>
                      )}
                      <CreateKeywordListButton />
                    </>
                  )}
                  <LoginButtonsComponent sentimentPage={sentimentPage} slackIntegrationPage={slackIntegrationPage} />
                </div>
              </div>
            ) : (
              <>
                {!isMobile && <>{noSpecificPages && <NavPages />}</>}
                <LoginButtonsComponent sentimentPage={sentimentPage} slackIntegrationPage={slackIntegrationPage} />
              </>
            )}
          </div>
        </>
      </header>
      {noSpecificPages && (
        <>
          <button
            className={`d-lg-none hamburger hamburger--collapse general-button`}
            type='button'
            ref={hamburguerMenu}
            aria-expanded={!isHiddenMenu}
            aria-label={isHiddenMenu ? 'Open main menu' : 'Close main menu'}
            onClick={() => {
              toggleMenu();
              hamburguerMenu.current.classList.toggle('is-active');
            }}
            style={{
              transform: `translateY(${isHiddenMenu ? 0 : calculateTransform()})`,
              top: `${headerContainer?.current?.getBoundingClientRect()?.top + 20}px`,
            }}
          >
            <span className='hamburger-box'>
              <span className='hamburger-inner'></span>
            </span>
          </button>
        </>
      )}
      <div
        className={`close-menu container-close-menu hide-lg ${isHiddenMenu ? 'd-none' : 'd-block'}`}
        onClick={() => {
          dispatch({ type: 'SHOW_HIDE_MENU', value: true });
          hamburguerMenu.current.classList.remove('is-active');
        }}
      />
      {isMobile && (
        <div className='header-mobile-sidenav'>
          <NavigationSidebar />
        </div>
      )}
      <div style={positionNav?.position === 'fixed' ? { height: positionNav.height } : null} />
    </>
  );
};

const SnowFallContainer = (props) => {
  const { isPlaying } = props;
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const homePage = location?.pathname === '/';

  return (
    <>
      {christmasCondition() && (
        <div
          className={`w-100 position-absolute top-0 overflow-hidden ${isAuthenticated ? '' : 'z-extra-max'}`}
          style={{
            height:
              isAuthenticated || (!isAuthenticated && !homePage)
                ? '100%'
                : document.querySelector('.home-animation-square-element')?.getBoundingClientRect()?.height,
            opacity: isPlaying ? 1 : 0,
            transition: 'opacity 1s ease-out',
            pointerEvents: 'none',
          }}
        >
          <SnowFall isPlaying={isPlaying} />
        </div>
      )}
    </>
  );
};

const LoginButtonsComponent = (props) => {
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth0();
  const { sentimentPage, slackIntegrationPage } = props;

  return (
    <>
      {!isMobile && (
        <>
          {sentimentPage || slackIntegrationPage ? (
            <SentimentButtons slackIntegrationPage={slackIntegrationPage} />
          ) : (
            <>{isAuthenticated ? <ProfileButton /> : <LoginButtons />}</>
          )}
        </>
      )}
    </>
  );
};

export { christmasCondition };
export default Navigation;
